<template>
    <div class="label-line-input-container report-line-text--interactive"
        :data-has-active-annotation="hasActiveAnnotation">
        <editor-content :editor="editor" />
    </div>
</template>

<script>
import { evtBus } from '@/main';
import { Editor, EditorContent } from '@tiptap/vue-2';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import History from '@tiptap/extension-history';

import LabelAnnotationsDisplay from './labelAnnotationsDisplayExtension.js';
import { reportUtils } from '@/features/report/lib/reportUtils';

import ReportNode from '@/features/report/models/ReportNode';
import LabelAnnotation from '@/features/report/models/LabelAnnotation';

export default {
    name: 'labelLineInput',
    components: {
        EditorContent
    },
    props: {
        value: {
            type: String,
            default: ''
        },
        labelReportNode: {
            type: ReportNode,
            required: true
        },
        annotations: {
            type: [LabelAnnotation, Array],
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        autoFocus: {
            type: Boolean,
            default: false
        }
    },
    
    data() {
        return {
            editor: null,
            hasAnnotations: this.labelReportNode.annotation.type != null,
            hasActiveAnnotation: false
        };
    },

    watch: {
        disabled(newVal) {
            if (this.editor) {
                this.editor.setOptions({
                    editable: !newVal
                });
            }
        },
        annotations: {
            handler(newVal) {
                if (this.editor) {
                    // console.log('watch: locations: updating label-annotations-display', newVal);
                    evtBus.$emit('evt_labelAnnotationsDisplay_update', { labelId: this.labelReportNode.id, annotations: newVal });
                }
            },
            immediate: true
        }
    },

    mounted() {
        this.editor = new Editor({
            content: this.value,
            autofocus: this.autoFocus ? 'end' : null,
            extensions: [
                Document,
                Paragraph,
                Text,
                History,
                LabelAnnotationsDisplay,
            ],
            editorProps: {
                attributes: {
                    class: 'label-line-input',
                }
            },
            onUpdate: ({ editor }) => {
                let htmlValue = editor.getHTML();
                if (this.hasAnnotations) {
                    // console.log('onUpdate: paragraphs', editor.getJSON().content?.filter(node => node.type === 'paragraph'));
                    const paragraphs = editor.getJSON().content?.filter(node => node.type === 'paragraph') || [];
                    if (!paragraphs.length || !paragraphs.some(paragraph => paragraph.content?.some(node => node.type === "LabelAnnotationsDisplay"))) {
                        // console.log('onUpdate: reinserting label-annotations-display');
                        editor.commands.insertContentAt(window.getSelection().anchorOffset + 1, `<span data-type="label-annotations-display" labelid="${this.labelReportNode.id}" annotationstext='${JSON.stringify(this.annotations)}'/>`, { updateSelection: true });
                        return;
                    }

                    const annotationsDisplayNodes = paragraphs.reduce((acc, paragraph) => acc.concat(paragraph.content?.filter(node => node.type === "LabelAnnotationsDisplay") || []), []);
                    
                    if (annotationsDisplayNodes.length > 0) {
                        // console.log('onUpdate: annotationsDisplayNodes', annotationsDisplayNodes);
                        const annotationsDisplayText = reportUtils.getAnnotationsText(this.annotations);
                        htmlValue = htmlValue.replace('data-type="label-annotations-display">', 'data-type="label-annotations-display">' + annotationsDisplayText);
                    }
                }
                // Limpa paragrafos vazios
                htmlValue = htmlValue.replace(/<p><\/p>/g, '');
                // console.log('onUpdate: emitting input', htmlValue);
                this.$emit('input', htmlValue);
            },
            onBlur: ({ editor, event }) => {
                // console.log('onBlur', event);
                this.$emit('blur', event);
            }
        })

        evtBus.$on('evt_annotationItem_onSelect', ({ annotationId }) => {
            if (!this.annotations || this.annotations.length === 0)
                return;
            // console.log('evt_annotationItem_onSelect', annotationId);
            this.hasActiveAnnotation = this.annotations.some(annotation => annotation.id === annotationId);
        });
        evtBus.$on('evt_annotationItem_onFocus', ({ annotationId }) => {
            if (!this.annotations || this.annotations.length === 0)
                return;
            // console.log('evt_annotationItem_onFocus', annotationId);
            this.hasActiveAnnotation = this.annotations.some(annotation => annotation.id === annotationId);
        });
        evtBus.$on('evt_annotationItem_onBlur', ({ annotationId }) => {
            // console.log('evt_annotationItem_onBlur', annotationId);
            this.hasActiveAnnotation = false;
        });
    },

    beforeDestroy() {
        if (this.editor) {
            // console.log('beforeDestroy: destroying editor');
            this.editor.destroy();
        }
    },
}
</script>

<style scoped>
:deep(.label-line-input) {
    /* width: min-content; */
    outline: none !important;
    /* display: inline-block; */
    font-size: var(--text-sm);
    line-height: var(--leading-report-text);
    background: transparent;
    border: none;
    border-bottom: 1px solid transparent;
    outline: none;
    color: inherit;
    padding: 0;
    margin: 0;
    margin-right: 2px;
    font-family: inherit;
    transition: border-bottom-color 0.2s ease, width 0.1s ease;
}
:deep(.label-line-input p) {
    margin: 0;
    /* display: inline-block; */
}

:deep(.label-line-input:not(:disabled)) {
    border-bottom-color: rgba(0, 0, 0, 0.4);
}

:deep(.label-line-input:not(:disabled):focus) {
    border-bottom-color: rgba(0, 0, 0, 0.8);
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
}

</style>